import * as React from 'react';

import type { BlockMathData } from '@gitbook/doc-core';
import type { BlockProps } from '@gitbook/slate-react';
import {
    Popover,
    PopoverRoot,
    usePopoverTriggerRef,
    usePopoverTriggerState,
} from '@gitbook/spine-react';

import {
    BlockLoadingFallback,
    BlockWrapper,
    useRestoreFocusFromPopover,
    VoidBlockFrame,
} from '../common';
import { MathEditPopover } from './MathEditPopover';

const MathTeXView = React.lazy(() => import('./MathTeXView'));

/**
 * Renderer for an math bock node.
 */
export function BlockMath(props: BlockProps): React.ReactElement {
    const { node, editor } = props;

    const trigger = usePopoverTriggerState();

    const openPopover = () => {
        editor.change((change) => {
            change.collapseToStartOf(node);
            change.blur();
        });

        trigger.open();
    };

    // When closing the popover, focus the editor.
    useRestoreFocusFromPopover(trigger, editor, node);

    return (
        <PopoverRoot {...trigger.rootProps}>
            <BlockWrapper {...props}>
                <VoidBlockFrame
                    {...props}
                    isFocused={props.isFocused || trigger.isOpen}
                    onPress={openPopover}
                >
                    <BlockMathContent {...props} />
                </VoidBlockFrame>
            </BlockWrapper>
            <Popover placement="bottom-start">
                <MathEditPopover editor={editor} node={node} />
            </Popover>
        </PopoverRoot>
    );
}

function BlockMathContent(props: BlockProps): React.ReactElement {
    const { node } = props;
    const { formula } = node.data.toFrozenObject() as BlockMathData;

    const ref = usePopoverTriggerRef<HTMLDivElement>();

    return (
        <div ref={ref} className="overflow-auto">
            <React.Suspense fallback={<BlockLoadingFallback />}>
                <MathTeXView formula={formula} inline={false} />
            </React.Suspense>
        </div>
    );
}
